import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { CaseMembership, LakeGrant } from '@/features/settings/types'
import { intlLocale } from '@/services/i18n/languages'
import { PermissionBadge } from './PermissionBadge'
import { RoleBadge } from './RoleBadge'

interface AsyncList<T> {
  items: T[]
  loading: boolean
  error: string | null
}

export interface AccessSectionProps {
  memberships: AsyncList<CaseMembership>
  lakeGrants: AsyncList<LakeGrant>
}

export function AccessSection({ memberships, lakeGrants }: AccessSectionProps) {
  const { t, i18n } = useTranslation()

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.access.caseMemberships.title')}</CardTitle>
          <CardDescription>{t('settings.access.caseMemberships.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          {memberships.loading ? (
            <p className="text-sm text-muted-foreground">
              {t('settings.access.caseMemberships.loading')}
            </p>
          ) : memberships.error ? (
            <p className="text-sm text-destructive">{memberships.error}</p>
          ) : memberships.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('settings.access.caseMemberships.empty')}
            </p>
          ) : (
            <div className="divide-y divide-border">
              {memberships.items.map(m => (
                <div
                  key={m.case_id}
                  className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                >
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-sm truncate">{m.case_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(m.joined_at).toLocaleDateString(intlLocale(i18n.language), {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                  <div className="ml-4">
                    <RoleBadge role={m.role} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.access.dataLake.title')}</CardTitle>
          <CardDescription>{t('settings.access.dataLake.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          {lakeGrants.loading ? (
            <p className="text-sm text-muted-foreground">{t('settings.access.dataLake.loading')}</p>
          ) : lakeGrants.error ? (
            <p className="text-sm text-destructive">{lakeGrants.error}</p>
          ) : lakeGrants.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('settings.access.dataLake.empty')}</p>
          ) : (
            <div className="divide-y divide-border">
              {lakeGrants.items.map(g => (
                <div
                  key={g.label_id}
                  className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: g.color ?? 'var(--muted-foreground)' }}
                    />
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-sm font-mono max-md:truncate">{g.label_name}</span>
                      {g.description && (
                        <span className="text-xs text-muted-foreground truncate">
                          {g.description}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4 shrink-0">
                    <span className="text-xs text-muted-foreground">L{g.level}</span>
                    <PermissionBadge permission={g.permission} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
